<template>
  <div class="rating-map">
    <div class="dashboards-filters">
      <div class="dashboards-filters-item">
        <strong>{{ title }}</strong>
      </div>
      <div class="dashboards-filters-date date-simple">
        <date-picker
          v-model="params.creation_date"
          format="D MMM"
          range-separator="-"
          :editable="false"
          :shortcuts="false"
          input-class="date-simple-input"
          range
          lang="en"
          class="dark"
        ></date-picker>
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.user_type"
          :multiple="false"
          :options="roleFieldList"
          placeholder="Select type role"
        />
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.country"
          :options="countriesList"
          field-name="CountryName"
          placeholder="Select country"
        />
      </div>

      <div class="dashboards-filters-item">
        <language-picker
          :options="languagesList"
          v-model="params.sourceLanguage"
          placeholder="Source language"
        ></language-picker>
      </div>

      <div class="dashboards-filters-item">
        <language-picker
          :options="languagesList"
          v-model="params.targetLanguage"
          placeholder="Target language"
        ></language-picker>
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.industry_field"
          :options="industryFieldList"
          field-name="Name"
          placeholder="Industry sector(s)"
        />
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.doc_type"
          :options="textCategoryList"
          field-name="Name"
          placeholder="Document type(s)"
        />
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.qaModel"
          :options="qaModelsList"
          field-name="Name"
          placeholder="QA model(s)"
        />
      </div>

      <div class="dashboards-filters-item">
        <filter-multiselect
          v-model="params.qaMethod"
          :options="qaMethodsList"
          field-name="Name"
          placeholder="Method(s)"
        />
      </div>

    </div>

    <div class="wrapper-map">
      <preloader :preloader-status="load"></preloader>

      <empty v-if="points.length == 0" :text="'No report was found'"></empty>

      <transition name="fade">
        <chart-map
          v-if="points.length > 0"
          :key="refreshKey"
          :points="points"
          @selectPie="selectPie"
        ></chart-map>
      </transition>
    </div>

    <transition name="fade">
      <div v-if="tooltipPie.length > 0" tabindex="1" class="rating-map-tooltip">
        <chart-pie
          :key="refreshKeyPie"
          :series="tooltipPie"
          tooltipTpl="statisticsTpl"
          data-labels="inside"
          class="report-statistics-graf-circle"
        ></chart-pie>
        <button @click="tooltipPie = []" type="button" class="close">
          <i class="mx-input-icon mx-clear-icon"></i>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import Vue from 'vue';
import DatePicker from 'vue2-datepicker';

import preloader from '@/components/preloader/index.vue';
import chartMap from '@/components/charts/chartMap/index.vue';
import empty from '@/components/emptyContainer/index.vue';

import helpers from '@/mixins/helpers';

import Api from '@/utils/api';

export default Vue.extend({
  name: 'MapPieChartItem',
  mixins: [helpers.work_with_date],
  props: {
    type: {
      type: String,
      default: () => '',
    },
    title: {
      type: String,
      default: () => 'KPI',
    },
  },
  components: {
    DatePicker,
    chartMap,
    preloader,
    empty,
    'chart-pie': () => import('./modalPieChart.vue'),
    'language-picker': () => import('@/components/selectors/language/index.vue'),
    'filter-multiselect': () => import('@/components/selectors/filterMultiple/index.vue'),
  },
  data () {
    return {
      points: [],
      tooltipPie: [],

      textCategoryList: [],
      industryFieldList: [],
      roleFieldList: [
        {
          ID: 'QA',
          Name: 'QA expert',
        },
        {
          ID: 'PR',
          Name: 'Proofreader',
        },
      ],
      managerList: [],
      languagesList: Array(),
      countriesList: Array(),
      qaMethodsList: Array(),
      qaModelsList: Array(),

      params: {
        user_type: {
          ID: 'PR',
          Name: 'Proofreader',
        },
        creation_date: Array(),
        industry_field: Array(),
        doc_type: Array(),
        manager: '',
        sourceLanguage: null,
        targetLanguage: null,
        country: Array(),
        qaModel: Array(),
        qaMethod: Array(),
      },

      refreshKeyPie: 0,

      offset: '-1',

      load: true,
      refreshKey: 0,
    };
  },
  watch: {
    params: {
      handler () {
        this.getPointMap();
      },
      deep: true,
    },
  },
  created () {
    this.getFilters().then(() => {
      this.getPointMap();
    });
  },
  methods: {
    /**
     * Method return string for multiselect
     *
     * @param {Number} count
     */
    limitText (count) {
      return `and ${count} others manager`;
    },

    refreshData () {
      this.load = true;
      this.points = [];
      this.tooltipPie = [];
    },

    selectItem (type, event) {
      this.params[type] = event;
      this.getPointMap();
    },

    getFilters () {
      return Promise.all([
        this.$store.dispatch('GET_LANGUAGES').then((data) => {
          this.languagesList = [...data];
          this.languagesList.unshift({
            ID: '',
            LanguageName: ' All',
            CountryISOCode: 'world',
          });
        }),
        this.$store.dispatch('GET_INDUSTRY').then((data) => {
          this.industryFieldList = [...data];
        }),
        this.$store.dispatch('GET_COUNTRIES').then((data) => {
          this.countriesList = [...data];
        }),
        this.$store.dispatch('GET_DOCUMENT_TYPES').then((data) => {
          this.textCategoryList = [...data];
        }),
        this.$store.dispatch('GET_QA_METHODS').then((data) => {
          this.qaMethodsList = [...data];
        }),
        Api.getEvaluationMethods().then((resp) => {
          if (resp.data.Status) {
            this.qaModelsList = [...resp.data.Data];
          }
        }),
      ]);
    },

    getPointMap () {
      this.refreshData();

      Api.dashboard.map.getPoints(this.transformParams()).then((data) => {
        if (data.data.Status) {
          data.data.Data.forEach((item) => {
            const arItem = [];

            if (
              item.GoodCount === 0 &&
              item.PoorCount === 0 &&
              item.IncorrectCount === 0 &&
              item.VeryGoodCount === 0 &&
              item.VeryPoorCount === 0
            ) {
              return;
            }

            arItem.push(item.CountryISOCode);
            arItem.push(item.VeryGoodCount !== 0 ? item.VeryGoodCount : null);
            arItem.push(item.GoodCount !== 0 ? item.GoodCount : null);
            arItem.push(item.PoorCount !== 0 ? item.PoorCount : null);
            arItem.push(item.VeryPoorCount !== 0 ? item.VeryPoorCount : null);
            arItem.push(item.IncorrectCount !== 0 ? item.IncorrectCount : null);
            arItem.push(this.offset);

            this.points.push(arItem);
          });
        } else {
          this.points = [];
        }

        this.load = false;
        this.refreshKey += 1;
      });
    },

    transformParams () {
      // let params = {
      //   user_type: this.params.user_type != "" ? this.params.user_type.ID : "",
      //   creation_date: this.convertDatePickerInUnix(this.params.creation_date),
      //   industry_field:
      //     this.params.industry_field != "" ? this.params.industry_field.ID : "",
      //   manager: this.params.manager ? this.params.manager.ID : "",
      //   doc_type: this.params.doc_type != "" ? this.params.doc_type.ID : ""
      // };

      const params = { ...this.params };
      params.creation_date = this.convertDatePickerInUnix(params.creation_date);
      Object.keys(params).forEach((key) => {
        if (Array.isArray(params[key])) {
          params[key] = params[key].map((i) => i.ID);
        } else if (params[key] instanceof Object) {
          params[key] = params[key].ID;
        }
      });

      return params;
    },

    selectPie (event) {
      this.tooltipPie = event.data;
      this.refreshKeyPie += 1;
    },
  },
});
</script>

<style lang="scss">

.rating-map {
  position: relative;
  height: 80vh;
  margin-bottom: 175px;

  .highcharts {
    &-container {
      box-shadow: $shadow;
    }
  }

  .dashboards-filters {
    flex-flow: row wrap;
    margin-bottom: 0;
    height: auto;
    // display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-gap: 21px;
    padding: 25px 2vw 25px 2vw;

    &-date {
      margin-left: 40px;
    }

    &-item {
      // min-width: 187px;
      // max-width: 187px;

      .multiselect {
        white-space: nowrap;
      }

      .lang-selector {
        width: unset;
      }
    }
  }

  &-tooltip {
    position: absolute;
    bottom: 10px;
    left: 20px;
    width: auto;
    padding: 1px;
    height: auto;
    background: #fff;
    border: 1px solid #313233;
    z-index: 1000;

    .close {
      position: absolute;
      top: 5px;
      right: 5px;
      border: 0;
      padding: 5px;
      display: block;
      font-size: 20px;
      background: #fff;
      z-index: 111111;

      i {
        color: #313233;
        transition: color 0.3s;

        &:hover {
          color: #cccccc;
        }
      }
    }
  }
}
.rating-map-control {
  margin-top: 20px;
}
.wrapper-map {
  position: relative;
  height: 100%;
}
</style>
